// QuickAddVisit.js — the one-off visit. Clicking an empty slot on the timeline
// opens this small modal: type OR speak the patient, and we create a single
// named (unassigned) conversation the SAME way the schedule importer does —
// speech runs through /v1/synapse/schedule/parse, typed text is used directly.
// No patient record is assigned; the name is the conversation name (the
// "name it in the first 2 minutes" backend trick doesn't fire reliably under
// multi-record, so we name it up front).
import React, { useEffect, useRef, useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { T, buttonPrimary, buttonGhost } from './liveTheme';

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// Compact time-of-day for the header (e.g. "8:30a").
const fmtClock = (ms) => {
  const d = new Date(ms);
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h < 12 ? 'a' : 'p';
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, '0')}${ap}`;
};

const QuickAddVisit = ({ onClose, startMs, endMs }) => {
  const { actions } = useComprehendLive();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Esc closes the modal.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const create = async (proposals) => {
    setBusy(true);
    setError(null);
    try {
      const res = await actions.createVisitsFromProposals(proposals);
      const created = res?.created || [];
      if (created.length) {
        // Pin the new visit(s) to the drawn window (start..end), so the card
        // lands there with that duration instead of at "now" (createdAt).
        if (Number.isFinite(startMs)) {
          created.forEach(
            (c) =>
              c.conversationId &&
              actions.setVisitWindow(c.conversationId, startMs, endMs),
          );
        }
        onClose?.();
      } else {
        setError("Couldn't create that — try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const createTyped = () => {
    const n = name.trim();
    if (!n) return;
    create([{ patientName: n }]);
  };

  const recordVoice = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const parts = [];
      const rec = new MediaRecorder(stream);
      recRef.current = rec;
      rec.ondataavailable = (e) => e.data.size > 0 && parts.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(parts, { type: rec.mimeType || 'audio/webm' });
        if (!blob.size) return;
        setBusy(true);
        setError(null);
        try {
          // Same magic as the importer: parse the clip into proposed visits.
          const rsp = await actions.parseSchedule({
            audio: await blobToDataURL(blob),
          });
          const visits = (rsp && !rsp.error && rsp.visits) || [];
          if (!visits.length) {
            setError("Didn't catch a name — try again.");
            setBusy(false);
            return;
          }
          await create(visits);
        } catch (e) {
          setError('Could not read that — try again.');
          setBusy(false);
        }
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      setError('Microphone unavailable.');
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000004,
        background: 'rgba(7,20,16,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 360,
          background: T.surfaceCard,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          padding: 16,
          boxShadow: '0 18px 50px rgba(0,0,0,0.5)',
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}
        >
          <span style={{ fontSize: 11, color: T.textFaint, fontWeight: 700 }}>
            NEW VISIT
            {Number.isFinite(startMs)
              ? ` · ${fmtClock(startMs)}${
                  Number.isFinite(endMs) ? `–${fmtClock(endMs)}` : ''
                }`
              : ''}
          </span>
          <i
            className="fa fa-times"
            aria-hidden="true"
            onClick={onClose}
            style={{
              marginLeft: 'auto',
              color: T.textSecondary,
              fontSize: 14,
              cursor: 'pointer',
              padding: 2,
            }}
          />
        </div>

        <input
          ref={inputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && createTyped()}
          placeholder="Patient name"
          style={{
            width: '100%',
            background: T.surfaceDeep,
            border: `1px solid ${T.border}`,
            borderRadius: 6,
            color: T.textPrimary,
            fontSize: 13,
            padding: '8px 10px',
            outline: 'none',
          }}
        />

        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <button
            style={{ ...buttonPrimary, flex: 1, opacity: busy ? 0.6 : 1 }}
            disabled={busy || !name.trim()}
            onClick={createTyped}
          >
            {busy ? 'Creating…' : 'Create visit'}
          </button>
          <button
            style={{
              ...buttonGhost,
              background: recording ? 'rgba(245,54,92,0.18)' : undefined,
              color: recording ? T.redSoft : undefined,
            }}
            disabled={busy}
            onClick={recordVoice}
            title="Say the patient name"
          >
            <i className="fa fa-microphone" aria-hidden="true" />{' '}
            {recording ? 'Stop' : 'Say it'}
          </button>
        </div>
        {error && (
          <p style={{ margin: '8px 0 0', fontSize: 11, color: T.redSoft }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default QuickAddVisit;
